import { Show, type Component } from 'solid-js';
import { Panel } from '~ui/panel';
import { Toolbar, ToolbarSection } from '~ui/toolbar';
import { Heading, Text } from '~ui/text';
import { Switch } from '~ui/switch';
import { Divider } from '~ui/divider';
import { NumberField, Row, StatRow } from '~ui/field';
import { formatCurrency, formatPercent, perYear } from '~/lib/format';
import { goalYear, payDownDebt, priceGrowth, setPayDownDebt, setPriceGrowth, setYears, summary, years, drip, setDrip } from '~/stores/calc-store';
import { goalProgress, profile } from '~/stores/profile-store';
import { deductibleInterest, leverage, monthlyIncome, portfolioYield, taxableIncome, totalBookCost, totalDeposits } from '~/stores/portfolio-store';
import { totalReceivedAmount, totalReturnAmount, totalRoc } from '~/stores/acb-store';

// Matches NumberBox's box width so the switches sit in the same vertical
// channel as the values in the fields above and the stats below.
const Toggle: Component<{ label: string; on: boolean; onChange: (next: boolean) => void }> = (props) => (
    <Row label={props.label}>
        <div class='flex h-full w-24 shrink-0 items-center justify-end px-2'>
            <Switch checked={props.on} onChange={props.onChange} label={props.label} />
        </div>
    </Row>
);

const SectionLabel: Component<{ children: string }> = (props) => (
    <div class='px-2 pt-2 pb-1'>
        <Heading level={2}>{props.children}</Heading>
    </div>
);

const Inputs: Component = () => (
    <Panel>
        <Toolbar>
            <ToolbarSection>
                <Heading level={2} class='pl-2'>
                    Assumptions
                </Heading>
            </ToolbarSection>
        </Toolbar>

        <div class='flex-1 min-h-0 overflow-y-auto scrollbar-hide'>
            {/* One width across every control, so the border-x rules stack into
                two continuous vertical seams rather than a ragged edge. */}
            <NumberField label='Years' value={years()} onValue={setYears} />
            <NumberField label='Price growth' value={priceGrowth()} onValue={setPriceGrowth} suffix='%' />

            <Toggle label='Reinvest (DRIP)' on={drip()} onChange={setDrip} />
            <Toggle label='Pay down loans first' on={payDownDebt()} onChange={setPayDownDebt} />

            <Divider orientation='horizontal' />

            <SectionLabel>Today</SectionLabel>
            <StatRow label='Market value' value={formatCurrency(summary().value)} />
            <StatRow label='Book cost' value={formatCurrency(totalBookCost())} />
            {/* Both, not one: book cost is what you paid and cost base is what
                it counts as after return of capital. Showing only the first
                leaves the Accounts table looking like it disagrees with the rail. */}
            <StatRow label='Return of capital' value={formatCurrency(totalRoc())} />
            <StatRow label='Cost base' value={formatCurrency(Math.max(0, totalBookCost() - totalRoc()))} />
            <StatRow label='Received' value={formatCurrency(totalReceivedAmount())} tone='green' />
            <StatRow
                label='Total return'
                value={formatCurrency(totalReturnAmount())}
                tone={totalReturnAmount() == null ? undefined : totalReturnAmount()! >= 0 ? 'green' : 'red'}
            />
            <StatRow label='Debt' value={formatCurrency(summary().debt)} />
            <StatRow label='Equity' value={formatCurrency(summary().equity)} />
            <StatRow label='Leverage' value={formatPercent(leverage(), 1)} />
            <StatRow label='Deposits' value={formatCurrency(totalDeposits())} />

            <Divider orientation='horizontal' />

            <SectionLabel>Income</SectionLabel>
            <StatRow label='Distributions' value={perYear(summary().income)} tone='green' />
            <StatRow label='Per month' value={formatCurrency(monthlyIncome())} tone='green' />
            {/* Only once there is a goal. Everything above is a fact about the
                book; this is the one row that says whether the facts are good,
                and it is worth nothing without a number to be measured against.

                Beside "Per month" rather than at the bottom, because it is the
                same figure read a second way — and because the assumptions
                directly above are what move the year it lands. */}
            <Show when={goalProgress() != null}>
                <StatRow label='Goal' value={`${formatCurrency(profile.goalMonthly)} · ${formatPercent(goalProgress(), 0)}`} />
                {/* Only when the plan reaches it. "not within 10 yr" is true and
                    useless — nobody holds a goal in that horizon, and it is the
                    one row here that reads as a verdict. */}
                <Show when={goalYear() != null}>
                    <StatRow label='Reached' value={goalYear() === 1 ? 'within a year' : `year ${goalYear()}`} />
                </Show>
            </Show>
            <StatRow label='Yield' value={formatPercent(portfolioYield(), 2)} />
            <StatRow label='Interest' value={perYear(summary().interest)} tone='red' />
            <StatRow label='Deductible' value={perYear(deductibleInterest())} />
            <StatRow label='Taxable income' value={perYear(taxableIncome())} />
            <StatRow label='Net (pre-tax)' value={perYear(summary().net)} tone={summary().net == null ? undefined : summary().net! >= 0 ? 'green' : 'red'} />

            <div class='h-2' />
        </div>
    </Panel>
);

export default Inputs;
